import type { ProcessMessage, UpdateResult } from './process'

/** The state variant(s) whose discriminant admits `Phase`. */
type PhaseState<State, Key extends keyof State, Phase> = State extends unknown
  ? Phase extends State[Key]
    ? State
    : never
  : never

/** The messages one phase accepts, each handled with its own narrowed type. */
export type TransitionRow<State, Message extends ProcessMessage, Result> = {
  [Type in Message['type']]?: (state: State, message: Extract<Message, { type: Type }>) => Result
}

/** Every phase of `State` (required) to the messages it accepts (optional). */
export type TransitionTable<
  State,
  Message extends ProcessMessage,
  Key extends keyof State,
  Result = UpdateResult<State, Message>
> = {
  [Phase in State[Key] & PropertyKey]: TransitionRow<PhaseState<State, Key, Phase>, Message, Result>
}

/**
 * Builds an `update` from a phase → message table.
 *
 * Leaving out a phase, or naming a phase or message type that does not exist,
 * is a compile error. A message the current phase does not list is an
 * impossible transition and throws; a legitimately late or duplicate message
 * gets an explicit row such as `(state) => state`. Rows shared by several
 * phases are written once and spread into each.
 */
export function transitions<
  State,
  Message extends ProcessMessage,
  Key extends keyof State,
  Result = UpdateResult<State, Message>
>(
  key: Key,
  table: NoInfer<TransitionTable<State, Message, Key, Result>>
): (state: State, message: Message) => Result {
  return (state, message) => {
    const phase = state[key] as State[Key] & PropertyKey
    const row = table[phase] as unknown as Record<
      string,
      ((state: State, message: Message) => Result) | undefined
    >
    const handler = row[message.type]
    if (handler == undefined) {
      throw new Error(`Unexpected ${message.type} while ${String(key)} is ${String(phase)}`)
    }
    return handler(state, message)
  }
}

/**
 * Picks the handler for `value`'s variant at `key`: the one-level sibling of
 * `transitions`, for tasks, selectors and anything else that reads a phase.
 * Leaving out a variant, or naming one that does not exist, is a compile
 * error; each handler sees its own narrowed variant.
 */
export function cases<Value, Key extends keyof Value, Result>(
  value: Value,
  key: Key,
  table: {
    [Variant in Value[Key] & PropertyKey]: (value: PhaseState<Value, Key, Variant>) => Result
  }
): Result {
  const variant = value[key] as Value[Key] & PropertyKey
  const handler = table[variant] as ((value: Value) => Result) | undefined
  if (handler == undefined) throw new Error(`Unexpected ${String(key)} ${String(variant)}`)
  return handler(value)
}

/**
 * A row in which these message types change nothing. Spread it into the
 * phases where they are legitimately late or duplicate, and say why there.
 */
export function ignore<Message extends ProcessMessage>(
  ...types: ReadonlyArray<Message['type']>
): { [Type in Message['type']]?: <State>(state: State) => State } {
  return Object.fromEntries(types.map((type) => [type, <State>(state: State) => state])) as {
    [Type in Message['type']]?: <State>(state: State) => State
  }
}
